// Performance Optimizations for Mondo Doro Theme

(function() {
  'use strict';

  // Intersection Observer for lazy loading and animations
  if ('IntersectionObserver' in window) {
    const observerOptions = {
      rootMargin: '50px 0px',
      threshold: 0.1
    };

    // Lazy load images and optimize animations
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const target = entry.target;
          
          // Add animation class for smooth entrance
          if (target.classList.contains('animate-on-scroll')) {
            target.classList.add('fade-in');
          }
          
          // Optimize images that come into view
          if (target.tagName === 'IMG' && target.dataset.src) {
            target.src = target.dataset.src;
            target.classList.remove('lazy');
            observer.unobserve(target);
          }
          
          // Add performance classes
          target.classList.add('critical-content');
        }
      });
    }, observerOptions);

    // Observe elements for optimization
    document.addEventListener('DOMContentLoaded', () => {
      const elementsToObserve = document.querySelectorAll('.animate-on-scroll, img[data-src], .product-card, .collection-card');
      elementsToObserve.forEach(el => observer.observe(el));
    });
  }

  // Debounce function for performance
  function debounce(func, wait, immediate) {
    let timeout;
    return function executedFunction() {
      const context = this;
      const args = arguments;
      const later = function() {
        timeout = null;
        if (!immediate) func.apply(context, args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func.apply(context, args);
    };
  }

  // Optimize scroll events
  const optimizedScroll = debounce(() => {
    // Add scroll optimization classes
    const scrolledElements = document.querySelectorAll('.header, .mobile-menu, .cart-drawer');
    scrolledElements.forEach(el => {
      if (el.classList.contains('scroll-container')) {
        el.classList.add('scroll-optimized');
      }
    });
  }, 16); // ~60fps

  // Optimize resize events
  const optimizedResize = debounce(() => {
    // Recalculate layout optimizations
    const layoutElements = document.querySelectorAll('.stable-layout');
    layoutElements.forEach(el => {
      el.classList.add('layout-optimized');
    });
  }, 250);

  // Optimize search functionality
  function optimizeSearch() {
    const searchInputs = document.querySelectorAll('input[type="search"], .search-input');
    searchInputs.forEach(input => {
      input.classList.add('search-optimized');
      
      // Debounce search input
      const debouncedSearch = debounce(() => {
        input.classList.add('searching');
        // Your search logic here
        setTimeout(() => {
          input.classList.remove('searching');
        }, 100);
      }, 300);
      
      input.addEventListener('input', debouncedSearch);
    });
  }

  // Optimize filter functionality
  function optimizeFilters() {
    const filterElements = document.querySelectorAll('.facets, .filter-group');
    filterElements.forEach(filter => {
      filter.classList.add('filter-optimized');
    });
  }

  // Optimize product grid
  function optimizeProductGrid() {
    const productGrids = document.querySelectorAll('.product-grid, .collection-grid');
    productGrids.forEach(grid => {
      grid.classList.add('product-grid-optimized');
    });
  }

  // Optimize navigation
  function optimizeNavigation() {
    const navElements = document.querySelectorAll('.header, .mobile-menu, .mega-menu');
    navElements.forEach(nav => {
      nav.classList.add('nav-optimized');
    });
  }

  // Optimize footer
  function optimizeFooter() {
    const footer = document.querySelector('.footer');
    if (footer) {
      footer.classList.add('footer-optimized');
    }
  }

  // Optimize carousels
  function optimizeCarousels() {
    const carousels = document.querySelectorAll('.slideshow, .owl-carousel, .slick-slider');
    carousels.forEach(carousel => {
      carousel.classList.add('carousel-optimized');
    });
  }

  // Optimize modals
  function optimizeModals() {
    const modals = document.querySelectorAll('.modal, .drawer, .popup');
    modals.forEach(modal => {
      modal.classList.add('modal-optimized');
    });
  }

  // Enhance logo hover effects
  function enhanceLogoHover() {
    const logoLinks = document.querySelectorAll('.logolink');
    
    logoLinks.forEach(logoLink => {
      const logoImg = logoLink.querySelector('.logoimg');
      
      if (logoImg) {
        // Add smooth hover animation
        logoLink.addEventListener('mouseenter', () => {
          logoLink.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
          logoImg.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        });
        
        // Add subtle bounce effect on hover
        logoLink.addEventListener('mouseenter', () => {
          logoLink.classList.add('logo-hover-active');
        });
        
        logoLink.addEventListener('mouseleave', () => {
          logoLink.classList.remove('logo-hover-active');
        });
      }
    });
  }

  // Translate Shopify payment button text to Hungarian
  function translatePaymentButton() {
    // Function to replace payment button text
    function replacePaymentButtonText() {
      const paymentButtons = document.querySelectorAll('[data-shopify="payment-button"], .shopify-payment-button__button');
      
      paymentButtons.forEach(button => {
        // Check if button contains "Buy it now" text
        if (button.textContent.includes('Buy it now') || button.textContent.includes('Buy now')) {
          // Replace the text content
          button.textContent = button.textContent.replace(/Buy it now|Buy now/g, 'Vedd meg most');
          
          // Also check for any child elements that might contain the text
          const buttonTextElements = button.querySelectorAll('span, div');
          buttonTextElements.forEach(element => {
            if (element.textContent.includes('Buy it now') || element.textContent.includes('Buy now')) {
              element.textContent = element.textContent.replace(/Buy it now|Buy now/g, 'Vedd meg most');
            }
          });
        }
      });
    }

    // Run immediately
    replacePaymentButtonText();
    
    // Run after a short delay to catch dynamically loaded buttons
    setTimeout(replacePaymentButtonText, 1000);
    
    // Run when DOM changes (for dynamic content)
    const observer = new MutationObserver(() => {
      replacePaymentButtonText();
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Initialize all optimizations
  function initPerformanceOptimizations() {
    optimizeSearch();
    optimizeFilters();
    optimizeProductGrid();
    optimizeNavigation();
    optimizeFooter();
    optimizeCarousels();
    optimizeModals();
    translatePaymentButton();
    enhanceLogoHover();
    
    // Add scroll and resize listeners
    window.addEventListener('scroll', optimizedScroll, { passive: true });
    window.addEventListener('resize', optimizedResize, { passive: true });
  }

  // Run optimizations when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPerformanceOptimizations);
  } else {
    initPerformanceOptimizations();
  }

  // Run optimizations after page load for dynamic content
  window.addEventListener('load', () => {
    // Re-run optimizations for any dynamically loaded content
    setTimeout(initPerformanceOptimizations, 100);
  });

})();
